(ns frontend.ux-overhaul.listing.generic.group-by
  "The Group by control of a listing. It is one button in the toolbar, with one popup that has
   two sections, as the mockup has: Columns by (horizontal, the Kanban only) and Rows by
   (vertical, every view mode that can group its rows: the swimlanes of the Kanban and the
   groups of the grid). Each section has a search box, a list of the fields to group by, the
   order of the groups, and a button that clears the section. The button of the control has a
   count of the sections that have a setting. The state of the settings and the events are the
   ones of the former controls Columns by and Rows by, so the saved views still load."
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.opensearch.facet-policy :as facet-policy]
   [common.opensearch.kanban-sort :as kanban-sort]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.listing-definition :as listing-definition]
   [frontend.ux-overhaul.listing.generic.event :as event]
   [frontend.ux-overhaul.listing.generic.toolbar :as toolbar-util]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.input.search :as search-input]
   [ui.components.radio.view :as radio]
   [ui.components.toggle.view :as toggle]
   [ui.components.toolbar-button.view :as toolbar-button]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.util :as uu]
   [ui.window :as window]))

(def column-view-modes
  "The view modes that have columns."
  #{:kanban})

(def row-view-modes
  "The view modes that group their rows. The list and the gallery do not get the field to group
   by yet. Add them here when they do."
  #{:kanban :grid})

(def panel-width
  "The width of the popup, in pixels. It is the same number as in `panel-class`."
  640)

(defn sections
  "The sections of the popup for a view mode, in order: `:columns`, then `:rows`. A section is
   there when the view mode has that grouping, and the listing has a field to group by or a
   grouping is set. The control has no section, and it is not shown, in a view mode that has
   neither grouping."
  [{:keys [view-mode groupable-fields group-by-field row-group-by-field]}]
  (let [fields? (boolean (seq groupable-fields))]
    (cond-> []
      (and (contains? column-view-modes view-mode)
           (or fields?
               (some? group-by-field)))
      (conj :columns)

      (and (contains? row-view-modes view-mode)
           (or fields?
               (some? row-group-by-field)))
      (conj :rows))))

(defn default-group-by-field
  "The field that the columns are grouped by when the user has not chosen one: the default of the
   entity type, when the listing can group by it, and else the first field that it can group by."
  [{:keys [entity-type groupable-fields]}]
  (let [configured (some-> entity-type
                           listing-definition/kanban-group-by-field
                           toolbar-util/field-path-normalized)]
    (or (some (fn [{:keys [field-path]}]
                (when (= field-path configured)
                  field-path))
              groupable-fields)
        (-> groupable-fields first :field-path))))

(defn columns-reset?
  "True when the columns are grouped by another field than the default, so that the section can
   put them back."
  [{:keys [group-by-field default-group-by-field]}]
  (boolean (and default-group-by-field
                (not= group-by-field default-group-by-field))))

(defn active-count
  "The number that the button shows: 1 for the columns when they are not grouped by the default
   field, and 1 for the rows when they are grouped. A view mode does not count a grouping that it does
   not have."
  [{:keys [view-mode row-group-by-field] :as args}]
  (+ (if (and (contains? column-view-modes view-mode)
              (columns-reset? args))
       1
       0)
     (if (and (contains? row-view-modes view-mode)
              (some? row-group-by-field))
       1
       0)))

(defn filter-fields
  "The fields whose label has the text `query`, without regard to case. All the fields, when the
   text is blank."
  [fields query]
  (let [query (string/lower-case (or query ""))]
    (if (string/blank? query)
      (vec fields)
      (->> fields
           (filter (fn [field]
                     (string/includes? (string/lower-case (or (toolbar-util/field-label field) ""))
                                       query)))
           vec))))

(defn popover-shift
  "The distance in pixels that the popup moves to the left so that it stays inside the window.
   It is 0 or less. The popup begins at the left edge of its button, `trigger-left`. The popup keeps
   `margin` pixels from the right edge, and it does not go to the left of the margin."
  [{:keys [trigger-left panel-width viewport-width margin]}]
  (let [margin (or margin 16)
        overflow (- (+ trigger-left panel-width) (- viewport-width margin))]
    (max (- margin trigger-left)
         (min 0 (- overflow)))))

(defn stage-group-by-field?
  "True when `field-path` is the stage field of one of the pipelines. The order of the stages is
   the order of the pipeline, never alphabetical or most frequent, so the order choices are
   replaced with a note for this field (the backend orders these columns by the stage order,
   whatever the user sends, see backend.domain.search.kanban)."
  [field-path]
  (pipeline/stage-attribute? (last field-path)))

(defn column-sort-mode-label
  [sort-mode]
  (case (kanban-sort/normalize-column-sort-mode sort-mode)
    :sort-by/most-hits (loc/tr :most-frequent)
    :sort-by/alphanumerical (loc/tr :sort-az)
    :sort-by/value-desc (loc/tr :sort-za)
    :sort-by/numeric (loc/tr :numeric-asc)
    :sort-by/numeric-desc (loc/tr :numeric-desc)
    (loc/tr :most-frequent)))

(defn row-sort-mode-label
  [sort-mode]
  (case (kanban-sort/normalize-row-sort-mode sort-mode)
    :sort-by/alphanumerical (loc/tr :sort-az)
    :sort-by/value-desc (loc/tr :sort-za)
    :sort-by/most-hits (loc/tr :most-frequent)
    :sort-by/least-hits (loc/tr :least-frequent)
    :sort-by/numeric (loc/tr :numeric-asc)
    :sort-by/numeric-desc (loc/tr :numeric-desc)
    (loc/tr :most-frequent)))

(defn column-sort-options
  "The orders of the columns, as pairs of the sort mode and its label. The numeric orders are
   only for a field that has numbers."
  [numeric-grouping?]
  (cond-> [[kanban-sort/alphanumerical (column-sort-mode-label kanban-sort/alphanumerical)]
           [kanban-sort/value-desc (column-sort-mode-label kanban-sort/value-desc)]
           [kanban-sort/most-hits (column-sort-mode-label kanban-sort/most-hits)]]
    numeric-grouping?
    (conj [kanban-sort/numeric (column-sort-mode-label kanban-sort/numeric)]
          [kanban-sort/numeric-desc (column-sort-mode-label kanban-sort/numeric-desc)])))

(defn row-sort-options
  "The orders of the rows, as pairs of the sort mode and its label."
  [numeric-grouping?]
  (cond-> [[kanban-sort/alphanumerical (row-sort-mode-label kanban-sort/alphanumerical)]
           [kanban-sort/value-desc (row-sort-mode-label kanban-sort/value-desc)]
           [kanban-sort/most-hits (row-sort-mode-label kanban-sort/most-hits)]
           [kanban-sort/least-hits (row-sort-mode-label kanban-sort/least-hits)]]
    numeric-grouping?
    (conj [kanban-sort/numeric (row-sort-mode-label kanban-sort/numeric)]
          [kanban-sort/numeric-desc (row-sort-mode-label kanban-sort/numeric-desc)])))

(defn column-sort-icon-class
  "The icon of an order of the columns."
  [sort-mode]
  (case (kanban-sort/normalize-column-sort-mode sort-mode)
    :sort-by/most-hits "fal fa-sort-amount-down"
    :sort-by/alphanumerical "fal fa-sort-alpha-down"
    :sort-by/value-desc "fal fa-sort-alpha-up"
    :sort-by/numeric "fal fa-sort-numeric-down"
    :sort-by/numeric-desc "fal fa-sort-numeric-up"
    "fal fa-sort-amount-down"))

(defn row-sort-icon-class
  "The icon of an order of the rows."
  [sort-mode]
  (case (kanban-sort/normalize-row-sort-mode sort-mode)
    :sort-by/most-hits "fal fa-sort-amount-down"
    :sort-by/least-hits "fal fa-sort-amount-up"
    :sort-by/alphanumerical "fal fa-sort-alpha-down"
    :sort-by/value-desc "fal fa-sort-alpha-up"
    :sort-by/numeric "fal fa-sort-numeric-down"
    :sort-by/numeric-desc "fal fa-sort-numeric-up"
    "fal fa-sort-amount-down"))

(defn- field-by-path
  [groupable-fields field-path]
  (some (fn [field]
          (when (= (:field-path field) field-path)
            field))
        groupable-fields))

(defn face-parts
  "The state that the button shows from 1536 px wide, as the former buttons Columns by and
   Rows by showed it: for each grouping that is set, a text with the field, the icon of the
   order of the groups, and for the columns the state of Hide empty. A pipeline stage field
   shows a lock in place of the order, because its order cannot change. A grouping that is
   not set, or that the view mode does not have, has no part. The result is a vector of maps
   with `:section`, `:text`, `:order-icon`, `:order-label` and, for the columns, `:hide-empty?`."
  [{:keys [view-mode groupable-fields group-by-field kanban-column-sort-mode hide-empty-columns?
           row-group-by-field row-group-sort-mode]}]
  (let [columns-field (when (contains? column-view-modes view-mode)
                        (field-by-path groupable-fields group-by-field))
        rows-field (when (contains? row-view-modes view-mode)
                     (field-by-path groupable-fields row-group-by-field))]
    (cond-> []
      columns-field
      (conj (let [stage? (stage-group-by-field? group-by-field)]
              {:section     :columns
               :text        (loc/tr :columns-by-field [(toolbar-util/field-label columns-field)])
               :hide-empty? (boolean hide-empty-columns?)
               :order-icon  (if stage?
                              "fal fa-lock"
                              (column-sort-icon-class kanban-column-sort-mode))
               :order-label (if stage?
                              (loc/tr :kanban-pipeline-stage-order)
                              (column-sort-mode-label kanban-column-sort-mode))}))

      rows-field
      (conj (let [stage? (stage-group-by-field? row-group-by-field)]
              {:section     :rows
               :text        (loc/tr :rows-by-field [(toolbar-util/field-label rows-field)])
               :order-icon  (if stage?
                              "fal fa-lock"
                              (row-sort-icon-class row-group-sort-mode))
               :order-label (if stage?
                              (loc/tr :kanban-pipeline-stage-order)
                              (row-sort-mode-label row-group-sort-mode))})))))

(def panel-class
  "The classes of the popup. It is under the button. `tw2-w-[640px]` is `panel-width`. The panes
   are one under the other, and they are side by side from 560 px (`min-[560px]`), because the two
   side panes need 368 px and the popup is at most the window width less 32 px. The popup is at most
   70 percent of the window height, and it scrolls up and down, because the stacked panes are taller than
   a small window. The classes of the
   layout and of the borders that depend on the width are classes of the old style sheet: the
   `min-[...]` variant does not work with a `tw2-` class (the check script finds the class dead).
   The background and the text use tokens, so the popup has the colors of a dark theme too."
  "tw2-absolute tw2-left-0 tw2-top-full tw2-z-50 tw2-mt-1 tw2-w-[640px] tw2-max-w-[calc(100vw-32px)] tw2-max-h-[70vh] tw2-overflow-x-hidden tw2-overflow-y-auto tw2-rounded-[16px] tw2-border tw2-border-dropdown-border-default tw2-bg-dropdown-list-bg tw2-text-primary tw2-shadow-[0_16px_36px_rgba(15,23,42,0.12)]")

(def tablist-class
  "The classes of the pane of the sections. It is above the other panes in a narrow popup, and
   at the left from 560 px, with the divider on the matching side."
  "tw2-flex tw2-flex-col tw2-gap-1 tw2-border-dropdown-border-default tw2-p-2 border-b min-[560px]:border-b-0 min-[560px]:border-r")

(def sort-pane-class
  "The classes of the pane of the order. It is under the other panes in a narrow popup, and at the
   right from 560 px, with the divider on the matching side."
  "tw2-flex tw2-min-w-0 tw2-flex-col tw2-gap-3 tw2-border-dropdown-border-default tw2-p-3 border-t min-[560px]:border-t-0 min-[560px]:border-l")

(defn panel-layout-class
  "The classes of the layout of the popup: one column, and from 560 px the columns of the panes
   (the sections, the fields and the order). A popup with one section has no pane of the sections."
  [{:keys [two-sections?]}]
  (str "tw2-grid grid-cols-1 "
       (if two-sections?
         "min-[560px]:grid-cols-[168px_minmax(0,1fr)_200px]"
         "min-[560px]:grid-cols-[minmax(0,1fr)_200px]")))

(def heading-class
  "tw2-px-3 tw2-text-xs tw2-font-semibold tw2-uppercase tw2-tracking-wide tw2-text-tertiary")

(defn radio-group-name
  "The name of the radios of one list in the popup of a listing. A name groups the radios of the whole
   document, so each listing, each section and each kind of list (`:field` or `:sort`) has its own."
  [{:keys [kind listing-id section]}]
  (str "group-by-" (name kind) "-" (pr-str listing-id) "-" (name section)))

(defn- choice-list
  "A list of choices, as the Radios of ZapKit in a group. `group-name` groups the radios.
   `choices` is a vector of maps with `:value` and `:label`. `checked` is the value that is
   chosen. `on-change` is called with the value of the choice that the user picks, and it is
   not called when `disabled?` is true."
  [{:keys [group-name checked choices on-change disabled?]}]
  (into [:div {:class "tw2-flex tw2-flex-col tw2-gap-2"}]
        (for [{:keys [value label]} choices]
          ^{:key (pr-str value)}
          [radio/view
           {:name      group-name
            :label     label
            :checked?  (= value checked)
            :disabled? disabled?
            :on-change (fn [checked?]
                         (when checked?
                           (on-change value)))}])))

(defn- hide-empty-icon
  [hide-empty-columns?]
  [(if hide-empty-columns? icons/eye-off-icon icons/eye-icon)
   {:size        16
    :strokeWidth 1.75
    :className   "tw2-shrink-0"}])

(defn- face-suffix
  "The state on the button, from the parts of `face-parts`. Nil when no grouping is set."
  [parts]
  (when (seq parts)
    (into [:span {:class "tw2-flex tw2-min-w-0 tw2-items-center tw2-gap-3"}]
          (for [{:keys [section text hide-empty? order-icon order-label]} parts]
            ^{:key (name section)}
            [:span {:class "tw2-flex tw2-min-w-0 tw2-items-center tw2-gap-1"}
             (when (= section :columns)
               [hide-empty-icon hide-empty?])
             [:span {:class "tw2-truncate"} text]
             [:span {:class "tw2-shrink-0 tw2-text-tertiary"} "·"]
             [:span
              {:class      "tw2-inline-flex tw2-shrink-0 tw2-items-center tw2-justify-center tw2-text-tertiary"
               :title      order-label
               :aria-label order-label}
              [:i {:class       order-icon
                   :aria-hidden "true"}]]]))))

(defn- section-state
  "The values that the popup shows for a section: its field, its order, its options, and the
   handlers of its events."
  [{:keys [section listing-id groupable-fields group-by-field kanban-column-sort-mode
           row-group-by-field row-group-sort-mode]}]
  (if (= section :columns)
    (let [selected-field (some (fn [{:keys [field-path] :as field}]
                                 (when (= field-path group-by-field)
                                   field))
                               groupable-fields)
          numeric? (contains? facet-policy/numeric-data-types (:field/data-type selected-field))]
      {:title          (loc/tr :columns-by)
       :aria-label     (loc/tr :kanban-column-grouping)
       :field-path     group-by-field
       :selected-field selected-field
       :sort-mode      kanban-column-sort-mode
       :sort-options   (column-sort-options numeric?)
       :stage?         (stage-group-by-field? group-by-field)
       :on-field       (fn [field-path]
                         (rf/dispatch [::event/set-group-by-field listing-id field-path]))
       :on-sort        (fn [sort-mode]
                         (rf/dispatch [::event/set-kanban-column-sort-mode listing-id sort-mode]))})
    (let [selected-field (some (fn [{:keys [field-path] :as field}]
                                 (when (= field-path row-group-by-field)
                                   field))
                               groupable-fields)
          numeric? (contains? facet-policy/numeric-data-types (:field/data-type selected-field))]
      {:title          (loc/tr :rows-by)
       :aria-label     (loc/tr :row-grouping)
       :field-path     row-group-by-field
       :selected-field selected-field
       :sort-mode      row-group-sort-mode
       :sort-options   (row-sort-options numeric?)
       :stage?         (stage-group-by-field? row-group-by-field)
       :on-field       (fn [field-path]
                         (rf/dispatch [::event/set-row-group-by-field listing-id field-path]))
       :on-sort        (fn [sort-mode]
                         (rf/dispatch [::event/set-row-group-sort-mode listing-id sort-mode]))})))

(defn- popover
  "The popup of the control."
  [{:keys [listing-id entity-type group-by-field hide-empty-columns? groupable-fields can-interact?
           section-keys active-section search* active-section* shift]
    :as props}]
  (let [default-field (default-group-by-field {:entity-type      entity-type
                                               :groupable-fields groupable-fields})
        default-label (some (fn [{:keys [field-path] :as field}]
                              (when (= field-path default-field)
                                (toolbar-util/field-label field)))
                            groupable-fields)
        state (section-state (assoc props :section active-section))
        columns? (= active-section :columns)
        clear-visible? (if columns?
                         (columns-reset? {:group-by-field         group-by-field
                                          :default-group-by-field default-field})
                         (some? (:field-path state)))
        clear-label (if (and columns?
                             (not (string/blank? default-label)))
                      (loc/tr :kanban-group-by-reset-with-label [default-label])
                      (loc/tr :clear-selection))
        fields (filter-fields groupable-fields @search*)
        two-sections? (> (count section-keys) 1)
        disabled? (not can-interact?)]
    [:div
     {:role       "dialog"
      :aria-label (loc/tr :group-by)
      :style      (when-not (zero? shift)
                    {:margin-left shift})
      :class      (str panel-class
                       " "
                       (panel-layout-class {:two-sections? two-sections?}))}
     (when two-sections?
       (into [:div
              {:role       "tablist"
               :aria-label (loc/tr :group-by)
               :class      tablist-class}]
             (for [section section-keys
                   :let [section-values (section-state (assoc props :section section))
                         selected? (= section active-section)
                         selected-label (some-> (:selected-field section-values) toolbar-util/field-label)]]
               ^{:key (str "group-by-section-" (name section))}
               [button/component
                {:variant       :ghost
                 :size          :sm
                 :type          "button"
                 :role          "tab"
                 :aria-selected selected?
                 :class         (uu/cm "tw2-h-auto tw2-w-full tw2-justify-between tw2-text-left"
                                       (when selected?
                                         "tw2-bg-btn-ghost-bg-hover"))
                 :on-click      (fn [_]
                                  (reset! active-section* section)
                                  (reset! search* ""))}
                [:span {:class "tw2-flex tw2-min-w-0 tw2-flex-col"}
                 [:span (:title section-values)]
                 [:span {:class "tw2-truncate tw2-text-xs tw2-font-normal tw2-text-tertiary"}
                  (or selected-label (loc/tr :none))]]
                [icons/chevron-right-icon
                 {:class       "tw2-h-4 tw2-w-4 tw2-shrink-0 tw2-text-tertiary"
                  :aria-hidden true}]])))
     [:div
      {:role       "tabpanel"
       :aria-label (:aria-label state)
       :class      "tw2-flex tw2-min-w-0 tw2-flex-col tw2-gap-3 tw2-p-3"}
      ;; The box must not grow in the column. A taller box puts the icon below
      ;; the input when only a few fields match.
      [search-input/view
       {:value         @search*
        :wrapper-class "tw2-flex-none"
        :on-change     (fn [value]
                         (reset! search* value))}]
      [:div {:class heading-class}
       (:title state)]
      (if (seq fields)
        [:div
         {:role       "radiogroup"
          :aria-label (:title state)
          :class      "tw2-max-h-[320px] tw2-overflow-y-auto"}
         [choice-list
          {:group-name (radio-group-name {:kind       :field
                                          :listing-id listing-id
                                          :section    active-section})
           :checked    (:field-path state)
           :choices    (mapv (fn [field]
                               {:value (:field-path field)
                                :label (toolbar-util/field-label field)})
                             fields)
           :disabled?  disabled?
           :on-change  (fn [field-path]
                         ((:on-field state) field-path)
                         (reset! search* ""))}]]
        [:div {:class "tw2-px-3 tw2-py-2 tw2-text-sm tw2-text-tertiary"}
         (loc/tr :no-groupable-fields-found)])
      [:div {:class "tw2-flex tw2-flex-wrap tw2-items-center tw2-justify-between tw2-gap-2 tw2-border-t tw2-border-dropdown-border-default tw2-pt-2"}
       (if clear-visible?
         [button/component
          {:variant   :ghost
           :size      :sm
           :type      "button"
           :disabled? disabled?
           :on-click  (fn [_]
                        (if columns?
                          (rf/dispatch [::event/set-group-by-field listing-id nil])
                          (rf/dispatch [::event/clear-row-group-by-field listing-id])))}
          clear-label]
         [:span])
       (when columns?
         [:div {:class "tw2-flex tw2-items-center tw2-gap-2 tw2-whitespace-nowrap tw2-text-sm tw2-text-secondary"}
          [hide-empty-icon hide-empty-columns?]
          [:span (loc/tr :hide-empty)]
          [toggle/toggle
           {:checked?   (boolean hide-empty-columns?)
            :disabled?  disabled?
            :aria-label (loc/tr :hide-empty)
            :on-change  (fn [hide-empty?]
                          (rf/dispatch [::event/set-hide-empty-columns listing-id hide-empty?]))}]])]]
     [:div {:class sort-pane-class}
      [:div {:class heading-class}
       (loc/tr :sort-by)]
      (if (:stage? state)
        [:div {:class "tw2-px-3 tw2-text-sm tw2-text-tertiary"}
         (loc/tr :kanban-pipeline-stage-order)]
        [:div {:role "radiogroup" :aria-label (loc/tr :sort-by)}
         [choice-list
          {:group-name (radio-group-name {:kind       :sort
                                          :listing-id listing-id
                                          :section    active-section})
           :checked    (:sort-mode state)
           :choices    (mapv (fn [[sort-mode label]]
                               {:value sort-mode
                                :label label})
                             (:sort-options state))
           :disabled?  disabled?
           :on-change  (:on-sort state)}]])]]))

(defn- summary
  "The text that says what the grouping is, for the tooltip of the button."
  [{:keys [group-by-field row-group-by-field groupable-fields view-mode]}]
  (let [label-of (fn [field-path]
                   (some (fn [field]
                           (when (= (:field-path field) field-path)
                             (toolbar-util/field-label field)))
                         groupable-fields))
        columns-label (when (contains? column-view-modes view-mode)
                        (some-> group-by-field label-of))
        rows-label (when (contains? row-view-modes view-mode)
                     (some-> row-group-by-field label-of))]
    (->> [(when columns-label
            (loc/tr :columns-by-field [columns-label]))
          (when rows-label
            (loc/tr :rows-by-field [rows-label]))]
         (remove nil?)
         (string/join ", "))))

(defn- popover-control
  [{:keys [can-interact?] :as props}]
  (r/with-let [open?* (r/atom false)
               search* (r/atom "")
               active-section* (r/atom nil)
               container-node* (atom nil)
               container-ref (fn [node]
                               (reset! container-node* node))
               escape-key-handler (toolbar-util/close-on-escape-handler open?*)
               outside-click-handler (fn [event]
                                       (let [container-node @container-node*
                                             event-target (dg/target event)]
                                         (when (and @open?*
                                                    container-node
                                                    (not (dg/node-contains? container-node event-target)))
                                           (reset! open?* false))))]
    (dg/add-event-listener! js/document "mousedown" outside-click-handler)
    (dg/add-event-listener! js/document "keydown" escape-key-handler)
    (let [section-keys (sections props)
          active-section (if (contains? (set section-keys) @active-section*)
                           @active-section*
                           (first section-keys))
          default-field (default-group-by-field props)
          count* (active-count (assoc props :default-group-by-field default-field))
          shift (if @open?*
                  (popover-shift {:trigger-left   (or (:left (dg/rect-map @container-node*)) 0)
                                  :panel-width    panel-width
                                  :viewport-width (or (:width @(rf/subscribe [::window/window-size]))
                                                      (dg/window-inner-width))})
                  0)]
      [:div
       {:class "tw2-relative tw2-shrink-0"
        :ref   container-ref}
       [toolbar-button/view
        {:label     (loc/tr :group-by)
         :icon      [:i.fal.fa-layer-group]
         :suffix    (face-suffix (face-parts props))
         :class     "tw2-max-w-[640px]"
         :badge     count*
         :title     (let [text (summary props)]
                      (when-not (string/blank? text)
                        text))
         :expanded? @open?*
         :disabled? (not can-interact?)
         :on-click  (fn [_]
                      (swap! open?* not))}]
       (when @open?*
         [popover (assoc props
                         :section-keys section-keys
                         :active-section active-section
                         :search* search*
                         :active-section* active-section*
                         :shift shift)])])
    (finally
      (dg/remove-event-listener! js/document "keydown" escape-key-handler)
      (dg/remove-event-listener! js/document "mousedown" outside-click-handler))))

(defn control
  "The Group by control, or nil when the listing is not ready or the view mode has no grouping.
   The arguments are the ones of the former controls Columns by and Rows by: `:listing-id`,
   `:entity-type`, `:view-mode`, `:initialized?`, `:group-by-field`, `:kanban-column-sort-mode`,
   `:hide-empty-columns?`, `:row-group-by-field`, `:row-group-sort-mode`, `:groupable-fields`
   and `:can-interact?`."
  [{:keys [initialized?] :as props}]
  (when (and initialized?
             (seq (sections props)))
    [popover-control props]))
